import React, { useId, useImperativeHandle, useState } from 'react';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { Badge, Group, Loader, Tooltip } from '@mantine/core';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import Autoload from '../../../../Autoload';
import PdfIcon from '../../../../../assets/images/module/general/PdfNewIcon.svg';
import uparrow from '../../../../../assets/images/module/general/SelectArrowUp.svg';
import downArrow from '../../../../../assets/images/module/general/SelectArrowDown.svg';
import { buildProtocolPdfBlob, sanitizeFileNamePart, showSmokeDetectorExportError } from '../../../../helpers/SmokeDetectorExport';
import { getGermanStatusMessages } from '../../../../../helpers/smokeDetectorHeadStatus';
import { SMOKE_DETECTOR_BADGE_COLORS } from '../../../../../helpers/smokeDetectorBadgeColors';
import SmokeDetectorProtocolTemplate from './SmokeDetectorProtocolTemplate';
import SmokeDetectorStatusBadges from './SmokeDetectorStatusBadges';

const tableCellStyle = {
  whiteSpace: 'nowrap',
  fontSize: '14px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: '400',
  background: 'white',
  borderTop: '8px solid #F7F7FA',
};

// Fixed so columns line up across every rental-unit table on the page — each table is a
// separate DOM table with auto content-based sizing by default, which drifts between
// sections whenever one has longer meter numbers/locations than another.
// rawValue ("Auslesewert") is commented out below rather than removed — keep it around for
// testing/debugging the head-status decode, might need to re-enable it.
const COLUMN_WIDTHS = { meterNo: '15%', meterName: '20%', location: '20%', /* rawValue: '13%', */ status: '45%' };

// Bubble/pill treatment for the expanded detail row below — font size/weight are left to the
// surrounding TableCell (13px, regular), only background/text color and shape come from here.
const detailBubbleStyle = ({ color, background }) => ({
  display: 'inline-block',
  padding: '4px 10px',
  borderRadius: '10px',
  color,
  backgroundColor: background,
});

// Row-expansion detail: same Fehler/Warnungen/Sonstiger-Status breakdown as the PDF and Excel
// exports, built from the same getGermanStatusMessages call, so all three stay in sync.
const DetectorDetailRow = ({ detector }) => {
  const messages = getGermanStatusMessages(detector.decodedStatus);
  const errorMessages = messages.filter((message) => message.severity === 'error').map((message) => message.message);
  const warningMessages = messages.filter((message) => message.severity === 'warning').map((message) => message.message);
  const batteryMessage = messages.find((message) => message.field === 'batteryVoltage')?.message;
  const dustMessage = messages.find((message) => message.field === 'dustLevel')?.message;
  const obstacleMessage = messages.find((message) => message.field === 'obstacleDistance')?.message;
  const errorColors = errorMessages.length > 0 ? SMOKE_DETECTOR_BADGE_COLORS.error : SMOKE_DETECTOR_BADGE_COLORS.ok;
  const warningColors = warningMessages.length > 0 ? SMOKE_DETECTOR_BADGE_COLORS.warning : SMOKE_DETECTOR_BADGE_COLORS.ok;

  return (
    <TableRow>
      <TableCell colSpan={4} style={{ background: 'white', borderTop: 'none', padding: '12px 16px 16px 32px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontWeight: 600, marginBottom: '6px' }}>Fehler und Warnungen</div>
        <Group gap={8} style={{ paddingLeft: '16px', marginBottom: '12px' }}>
          <div style={detailBubbleStyle(errorColors)}>Fehler: {errorMessages.length > 0 ? errorMessages.join(', ') : 'keine Fehler'}</div>
          <div style={detailBubbleStyle(warningColors)}>Warnungen: {warningMessages.length > 0 ? warningMessages.join(', ') : 'keine Warnungen'}</div>
        </Group>
        <div style={{ fontWeight: 600, marginBottom: '6px' }}>Sonstiger Status</div>
        <Group gap={8} style={{ paddingLeft: '16px' }}>
          <div style={detailBubbleStyle(SMOKE_DETECTOR_BADGE_COLORS.neutral)}>Batterie: {batteryMessage}</div>
          <div style={detailBubbleStyle(SMOKE_DETECTOR_BADGE_COLORS.neutral)}>Staubpegel: {dustMessage}</div>
          <div style={detailBubbleStyle(SMOKE_DETECTOR_BADGE_COLORS.neutral)}>Hindernis: {obstacleMessage}</div>
        </Group>
      </TableCell>
    </TableRow>
  );
};

const SmokeDetectorRentalUnitGroup = React.forwardRef(({ rentalUnitName, detectors, readingDateTime, propertySelected, profileData, accountData, icon = null }, ref) => {
  // Sum of flagged conditions across all detectors, not the count of detectors that have at
  // least one — a single detector with 2 error fields counts as 2, not 1 (same fix as
  // SmokeDetectorDataHandler.buildReadingSummary).
  const errorCount = detectors.reduce((sum, detector) => sum + detector.severity.errorFields.length, 0);
  const warningCount = detectors.reduce((sum, detector) => sum + detector.severity.warningFields.length, 0);
  const templateId = `smoke-detector-protocol-${useId()}`;
  const [exporting, setExporting] = useState(false);
  const [expandedDetectorIds, setExpandedDetectorIds] = useState(() => new Set());

  const toggleExpanded = (detectorId) => {
    setExpandedDetectorIds((prev) => {
      const next = new Set(prev);
      if (next.has(detectorId)) next.delete(detectorId);
      else next.add(detectorId);
      return next;
    });
  };

  const fileBaseName = () => {
    const safeUnitName = sanitizeFileNamePart(rentalUnitName);
    const dateStr = readingDateTime ? dayjs(readingDateTime).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    return `Rauchwarnmelder-Protokoll_${safeUnitName}_${dateStr}`;
  };

  // Shared by the single-unit download button below and by the bulk/zip export triggered from
  // the parent reading accordion (via the imperative handle) — both need the same PDF blob, just
  // with different download behavior afterwards.
  const generatePdfBlob = async () => {
    const element = document.getElementById(templateId);
    if (!element) throw new Error('Protocol template not found in DOM');
    const blob = await buildProtocolPdfBlob(element, { authToken: Autoload.Utils.getAuthToken(), profileData, accountData });
    return { blob, fileName: `${fileBaseName()}.pdf` };
  };

  useImperativeHandle(ref, () => ({
    exportBlob: async () => {
      try {
        return await generatePdfBlob();
      } catch (e) {
        return null;
      }
    },
  }));

  const handleDownloadClick = async () => {
    setExporting(true);
    try {
      const { blob, fileName } = await generatePdfBlob();
      saveAs(blob, fileName);
    } catch (e) {
      showSmokeDetectorExportError();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <Group gap={10} mb={10} wrap="nowrap">
        {icon && <img src={icon} alt=""/>}
        <span style={{ fontSize: '16px', fontWeight: '600', color: '#0E0E1D', fontFamily: 'Inter, sans-serif' }}>
          {rentalUnitName || 'Ohne Wohneinheit'}
        </span>
        {errorCount > 0 && (
          <Badge
            className='smoke-detector-screen-badge'
            size="sm"
            style={{ color: SMOKE_DETECTOR_BADGE_COLORS.error.color, backgroundColor: SMOKE_DETECTOR_BADGE_COLORS.error.background }}
          >
            {errorCount} Fehler
          </Badge>
        )}
        {warningCount > 0 && (
          <Badge
            className='smoke-detector-screen-badge'
            size="sm"
            style={{ color: SMOKE_DETECTOR_BADGE_COLORS.warning.color, backgroundColor: SMOKE_DETECTOR_BADGE_COLORS.warning.background }}
          >
            {warningCount} Warnung{warningCount > 1 ? 'en' : ''}
          </Badge>
        )}
        <Group gap={5} wrap="nowrap" style={{ marginLeft: 'auto' }}>
          <span style={{ color: '#454576', fontSize: '14px' }}>Herunterladen:</span>
          <Tooltip label="Als PDF herunterladen" withArrow>
            <IconButton
              aria-label="Als PDF herunterladen"
              disabled={exporting}
              onClick={handleDownloadClick}
              style={{ background: 'white', border: '1px solid #DADAE5', boxShadow: '0 1px 2px 0 rgba(14, 14, 29, .06)', padding: '5px' }}
            >
              {exporting ? <Loader size="xs" /> : <img src={PdfIcon} width="15px" alt="PDF" />}
            </IconButton>
          </Tooltip>
        </Group>
      </Group>
      <SmokeDetectorProtocolTemplate
        id={templateId}
        rentalUnitName={rentalUnitName}
        detectors={detectors}
        readingDateTime={readingDateTime}
        propertySelected={propertySelected}
      />
      <TableContainer>
        <Table style={{ border: 'none', tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell width={COLUMN_WIDTHS.meterNo} style={{ border: 'none', fontSize: '12px', fontWeight: '400', color: '#727293', paddingBottom: 0 }}>
                Rauchwarnmelder (Nr.)
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.meterName} style={{ border: 'none', fontSize: '12px', fontWeight: '400', color: '#727293', paddingBottom: 0 }}>
                Bezeichnung
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.location} style={{ border: 'none', fontSize: '12px', fontWeight: '400', color: '#727293', paddingBottom: 0 }}>
                Standort
              </TableCell>
              {/* Auslesewert (raw headStatus) — hidden for now, kept for testing/debugging the decode.
              <TableCell width={COLUMN_WIDTHS.rawValue} style={{ border: 'none', fontSize: '12px', fontWeight: '400', color: '#727293', paddingBottom: 0 }}>
                Auslesewert
              </TableCell>
              */}
              <TableCell width={COLUMN_WIDTHS.status} style={{ border: 'none', fontSize: '12px', fontWeight: '400', color: '#727293', paddingBottom: 0 }}>
                Ergebnis
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detectors.map((detector) => {
              const isExpanded = expandedDetectorIds.has(detector.detectorId);
              return (
                <React.Fragment key={detector.detectorId}>
                  <TableRow
                    style={{ background: '#F7F7FA', height: '56px', cursor: 'pointer' }}
                    onClick={() => toggleExpanded(detector.detectorId)}
                  >
                    <TableCell width={COLUMN_WIDTHS.meterNo} style={tableCellStyle}>
                      <Group gap={6} wrap="nowrap">
                        <img src={isExpanded ? uparrow : downArrow} alt="" style={{ minWidth: '8px' }} />
                        {detector.meterNo || '-'}
                      </Group>
                    </TableCell>
                    <TableCell width={COLUMN_WIDTHS.meterName} style={tableCellStyle}>{detector.meterName || '-'}</TableCell>
                    <TableCell width={COLUMN_WIDTHS.location} style={tableCellStyle}>{detector.location || '-'}</TableCell>
                    {/* Auslesewert (raw headStatus) — hidden for now, kept for testing/debugging the decode.
                    <TableCell width={COLUMN_WIDTHS.rawValue} style={{ ...tableCellStyle, color: '#727293' }}>
                      {detector.rawHeadStatus}
                    </TableCell>
                    */}
                    <TableCell width={COLUMN_WIDTHS.status} style={{ ...tableCellStyle, whiteSpace: 'normal' }}>
                      <SmokeDetectorStatusBadges decodedStatus={detector.decodedStatus} />
                    </TableCell>
                  </TableRow>
                  {isExpanded && <DetectorDetailRow detector={detector} />}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

SmokeDetectorRentalUnitGroup.displayName = 'SmokeDetectorRentalUnitGroup';

export default SmokeDetectorRentalUnitGroup;
